import { useMemo, useState } from 'react'
import { useGetAuthUserList } from '@/service-library/hooks/auth-user'
import { useGetMetricReport } from '@/service-library/hooks/metrics'
import { AverageMetricReport } from '@/types/workflow-metric-reports'
import ListAutocomplete, {
  BaseDataType,
} from '@/components/list-autocomplete/ListAutocomplete'
import AverageMetricBarChart from '@/components/project-metrics-dashboard/AverageMetricBarChart'
import ChartCard from '@/components/project-metrics-dashboard/ChartCard'
import { CommonQueryOptions } from '@/components/project-metrics-dashboard/types'

const ALL_USERS: BaseDataType = { id: 'no-user-id', name: 'All' }

type AverageHumanInteractionCardProps = {
  commonQueryOptions: CommonQueryOptions
}

export default function AverageHumanInteractionCard({
  commonQueryOptions,
}: AverageHumanInteractionCardProps) {
  const [user, setUser] = useState<BaseDataType>(ALL_USERS)
  const { users, isPending } = useGetAuthUserList({
    filters: { fields__only: 'id,name' },
  })

  const { report: userInteractionAverageReport } =
    useGetMetricReport<AverageMetricReport>({
      ...commonQueryOptions,
      reportKey: 'user-interaction-average',
      filters: {
        ...commonQueryOptions.filters,
        user_id: user.id === ALL_USERS.id ? undefined : user.id,
      },
    })

  const userOptions = useMemo(
    () => [
      ALL_USERS,
      // The metrics API filters by pd user id. A pending user (invited, never
      // logged in) has no id yet, so they are left out.
      ...users
        .flatMap(({ id, name }) => (id ? [{ id, name }] : []))
        .sort((a, b) => a.name.localeCompare(b.name)),
    ],
    [users],
  )

  return (
    <ChartCard
      heading="Average Human Interaction"
      headingTooltip={
        <>
          The average time a human spends interacting with a document in this
          workflow state.
          <br />
          <br />
          This does not include time spent with the document open in an idle
          tab. This only includes time actively spent interacting with the
          document.
        </>
      }
      controls={
        <ListAutocomplete
          autoSelect={false}
          selected={user}
          setSelected={setUser}
          label="User"
          options={userOptions}
          loading={isPending}
          variant="outlined"
          sx={{ maxWidth: 250 }}
        />
      }
    >
      <AverageMetricBarChart
        report={userInteractionAverageReport}
        type="user-interaction-average"
      />
    </ChartCard>
  )
}
